import { computed, ref } from 'vue';
import { concat } from 'lodash-es';
import type { PassengerKey, Passenger } from '@aviata/booking-data';
import type { Airflow } from '@aviata/platform-types';
import { citizenshipEntity } from 'shared';

import { savedPassengersConfig } from '@/features/saved-passengers';

import { sentryModel } from '@/shared/model';

import type { PassengerWithDocType } from './types';

// ==================== Валидация документов (standalone) ====================
export const checkIfPassengerHasRequiredDocumentFields = (
  updated: PassengerWithDocType,
) => {
  const isCountryWithoutSeries =
    citizenshipEntity.config.countriesWithoutSeries.includes(
      updated.citizenship,
    );

  const hasBasicDocumentFields =
    updated.document.givenAt &&
    (isCountryWithoutSeries || updated.document.series) &&
    updated.document.organization &&
    updated.document.expiresAt;

  if (updated.citizenship === citizenshipEntity.config.AZERBAIJAN_CODE) {
    return hasBasicDocumentFields && updated.fin;
  }

  return hasBasicDocumentFields;
};

export function usePassengersForm(schema: Airflow.Offer.Schema.Passengers) {
  // ==================== Работа с сохраненными пассажирами ====================
  const savedPassengers = ref<Passenger[]>([]);
  const localPassengers = ref<Passenger[]>([]);

  function getLocalPassengers(): Passenger[] {
    try {
      const raw = sessionStorage.getItem(
        savedPassengersConfig.SESSION_STORAGE_KEY,
      );
      const localPassengersList = raw ? JSON.parse(raw) : [];
      return localPassengersList.filter((pax: Passenger) => {
        return !savedPassengers.value.some(
          savedPax => savedPax.document.number === pax.document.number,
        );
      });
    } catch (e) {
      sentryModel.captureException(
        sentryModel.getReadableError(
          'ERR_FAILED_TO_PARSE_LOCAL_PASSENGERS',
          e as Error,
        ),
      );
      return [];
    }
  }

  const combinedPassengers = computed(() =>
    concat(savedPassengers.value, localPassengers.value),
  );

  const handleSavedPassengerUpdate = (
    idx: number,
    updatedPassenger: Passenger,
  ) => {
    savedPassengers.value[idx] = { ...updatedPassenger };
  };

  const updateLocalPassenger = (idx: number, updated: Passenger) => {
    const localIdx = idx - savedPassengers.value.length;
    localPassengers.value[localIdx] = { ...updated };
    sessionStorage.setItem(
      savedPassengersConfig.SESSION_STORAGE_KEY,
      JSON.stringify(localPassengers.value),
    );
  };

  // ==================== Управление активным пассажиром ====================
  const activePassengerKey = ref<PassengerKey | null>(null);
  const activeSelectedPassenger = ref<Passenger | null>(null);
  const passengerIdxToEdit = ref<number | null>(null);
  const passengerOnCardForForm = ref<Passenger | null>(null);
  const isNewPassenger = ref(false);

  const activePassengerType = computed(() => {
    return activePassengerKey.value?.slice(0, 3) || '';
  });

  const activeSchema = computed(() => {
    return activePassengerKey.value ? schema[activePassengerKey.value] : null;
  });

  return {
    // Работа с сохраненными пассажирами
    savedPassengers,
    localPassengers,
    combinedPassengers,
    getLocalPassengers,
    handleSavedPassengerUpdate,
    updateLocalPassenger,

    // Управление активным пассажиром
    activePassengerKey,
    activeSelectedPassenger,
    passengerIdxToEdit,
    passengerOnCardForForm,
    isNewPassenger,
    activePassengerType,
    activeSchema,

    // Валидация
    checkIfPassengerHasRequiredDocumentFields,
  };
}
